import React, { useEffect, useMemo, useState } from "react";
import usePrefersReducedMotion from "../../hooks/usePrefersReducedMotion";
import "./AnimatedStoryText.css";

const DEFAULT_WORD_MS = 80;
const NARRATION_WAIT_FALLBACK_MS = 1800;

const splitWords = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return [];
  return trimmed.split(/\s+/).filter(Boolean);
};

const splitSentences = (text) => {
  const trimmed = (text || "").trim();
  if (!trimmed) return [];
  return trimmed.split(/(?<=[.!?])\s+/).filter(Boolean);
};

/**
 * Progressive word reveal for storybook pages.
 * Uses one-shot CSS animation-delay per word (no per-frame React re-renders).
 */
const AnimatedStoryText = ({
  text,
  animationSpeed = DEFAULT_WORD_MS,
  enableAnimation = true,
  withDropCap = false,
  variant = "storybook", // "storybook" | "plain"
  waitForNarration = false,
  narrationActive = false,
  className = "",
  style,
}) => {
  const prefersReducedMotion = usePrefersReducedMotion();
  const shouldAnimate = enableAnimation && !prefersReducedMotion;
  const wordMs = Math.min(100, Math.max(60, Number(animationSpeed) || DEFAULT_WORD_MS));

  const [canReveal, setCanReveal] = useState(!waitForNarration);

  useEffect(() => {
    setCanReveal(!waitForNarration);
  }, [text, waitForNarration]);

  useEffect(() => {
    if (!waitForNarration) {
      setCanReveal(true);
      return undefined;
    }
    if (narrationActive) {
      setCanReveal(true);
      return undefined;
    }
    const timer = setTimeout(() => setCanReveal(true), NARRATION_WAIT_FALLBACK_MS);
    return () => clearTimeout(timer);
  }, [waitForNarration, narrationActive, text]);

  const sentences = useMemo(() => splitSentences(text), [text]);
  const plainWords = useMemo(() => splitWords(text), [text]);

  if (!text?.trim()) return null;

  // Reduced motion / animation off: gentle paragraph fade only.
  if (!shouldAnimate) {
    const reducedClass = enableAnimation
      ? "animated-story-text animated-story-text--reduced-fade"
      : "animated-story-text";

    if (variant === "plain") {
      return (
        <p className={`${reducedClass} ${className}`.trim()} style={style}>
          {text}
        </p>
      );
    }

    const first = sentences[0] || text.trim();
    const rest = sentences.slice(1);
    return (
      <div className={`storybook-text-block ${reducedClass} ${className}`.trim()} style={style}>
        {withDropCap ? (
          <p className="storybook-line storybook-line--first">
            <span className="storybook-drop-cap">{first.charAt(0)}</span>
            {first.slice(1)}
          </p>
        ) : (
          <p className="storybook-line storybook-line--first">{first}</p>
        )}
        {rest.length > 0 && (
          <>
            <div className="storybook-divider" aria-hidden="true">
              <span className="storybook-divider-line" />
              <span className="storybook-heart">♥</span>
              <span className="storybook-divider-line" />
            </div>
            <p className="storybook-line">{rest.join(" ")}</p>
          </>
        )}
      </div>
    );
  }

  const renderWordSpan = (word, globalIndex, { dropCap = false } = {}) => {
    const delay = canReveal ? `${globalIndex * wordMs}ms` : undefined;
    const wordClass = canReveal ? "animated-story-word" : "animated-story-word animated-story-word--waiting";

    if (dropCap && word.length > 0) {
      return (
        <React.Fragment key={`w-${globalIndex}`}>
          <span
            aria-hidden="true"
            className={`storybook-drop-cap ${wordClass}`}
            style={delay != null ? { animationDelay: delay } : undefined}
          >
            {word.charAt(0)}
          </span>
          {word.length > 1 && (
            <span
              aria-hidden="true"
              className={wordClass}
              style={delay != null ? { animationDelay: delay } : undefined}
            >
              {word.slice(1)}
            </span>
          )}
          {" "}
        </React.Fragment>
      );
    }

    return (
      <React.Fragment key={`w-${globalIndex}`}>
        <span
          aria-hidden="true"
          className={wordClass}
          style={delay != null ? { animationDelay: delay } : undefined}
        >
          {word}
        </span>
        {" "}
      </React.Fragment>
    );
  };

  if (variant === "plain") {
    return (
      <p
        className={`animated-story-text animated-story-text--page-enter ${className}`.trim()}
        style={style}
        aria-label={text}
      >
        <React.Fragment key={`${text}-${canReveal ? "go" : "wait"}`}>
          {plainWords.map((word, i) => renderWordSpan(word, i))}
        </React.Fragment>
      </p>
    );
  }

  // Storybook layout: keep first-sentence / divider / rest structure.
  const firstSentence = sentences[0] || "";
  const restSentences = sentences.slice(1);
  const firstWords = splitWords(firstSentence);
  const restWords = splitWords(restSentences.join(" "));

  let globalIndex = 0;

  return (
    <div
      className={`storybook-text-block animated-story-text ${className}`.trim()}
      style={style}
      aria-label={text}
    >
      <div key={`${text}-${canReveal ? "go" : "wait"}`} className="animated-story-text--page-enter">
      <p className="storybook-line storybook-line--first">
        {firstWords.map((word, i) => {
          const idx = globalIndex++;
          return renderWordSpan(word, idx, { dropCap: withDropCap && i === 0 });
        })}
      </p>
      {restWords.length > 0 && (
        <>
          <div
            className={`storybook-divider ${canReveal ? "animated-story-divider" : "animated-story-divider animated-story-divider--waiting"}`}
            aria-hidden="true"
            style={
              canReveal
                ? { animationDelay: `${Math.max(0, firstWords.length - 1) * wordMs}ms` }
                : undefined
            }
          >
            <span className="storybook-divider-line" />
            <span className="storybook-heart">♥</span>
            <span className="storybook-divider-line" />
          </div>
          <p className="storybook-line">
            {restWords.map((word) => {
              const idx = globalIndex++;
              return renderWordSpan(word, idx);
            })}
          </p>
        </>
      )}
      </div>
    </div>
  );
};

export default AnimatedStoryText;
